/**
 * Where the standard MCQ game's on-screen furniture sits, as a function of the viewport.
 *
 * The answer tiles are drawn INSIDE the Unity canvas, not the DOM, so nothing in the browser can
 * measure them - `UnityPlayer` only sends DOM positions the other way (UpdateComponentPositions).
 * The numbers below were therefore measured off screenshots of the running game (1728x905, scale
 * 0.838) rather than read from the engine, the same way the speech bubble's hand-tuned offset was
 * arrived at.
 *
 * This module exists so that estimate lives in exactly one place. If the Unity build ever reports
 * the real rects, or the game team supplies the canvas scaler maths, only the internals here change
 * and every caller keeps working.
 */

// The resolution the game's UI is laid out against. Unity scales that layout to fit the window, so
// positions are expressed in these reference pixels and scaled below.
const REFERENCE_WIDTH = 1920
const REFERENCE_HEIGHT = 1080

/**
 * Vertical offsets are measured from the VIEWPORT CENTRE (positive = above it) in reference pixels,
 * because the game centres its UI. The four tiles straddle the centre line, sitting slightly BELOW
 * it - hence the negative gap offset.
 */
const ANSWER_GAP_CENTRE_OFFSET = -86
const ANSWER_AREA_TOP_OFFSET = 126
/** Half the clear band between the two rows of tiles: gap centre +/- this is where they start. */
const ANSWER_BAND_HALF_HEIGHT = 91
/**
 * Distance from the centre line out to the tiles' INNER edges. The tiles sit either side of a wide
 * empty middle column, and this is the half-width of that column - what the bubble has to fit in.
 */
const ANSWER_INNER_EDGE = 433

// The speech bubble's fixed footprint (native SVG art, see DaxSpeechBubble in MultipleChoiceQuestions).
// Kept here, not there, because the geometry below needs it: the bubble doesn't scale down with the
// window, so it's what determines how the bubble's resting position is clamped as the tiles close in.
export const SPEECH_BUBBLE_WIDTH = 304
export const SPEECH_BUBBLE_HEIGHT = 146

export interface UnityAnswerLayout {
  /** Centre of the gap between the four answer tiles, in px from the BOTTOM of the viewport. */
  gapCentreY: number
  /** Top edge of the answer area, in px from the bottom of the viewport. */
  answersTopY: number
  /** Bottom edge of the top row of tiles, in px from the bottom of the viewport. */
  bandTopY: number
  /** Top edge of the bottom row of tiles, in px from the bottom of the viewport. */
  bandBottomY: number
  /** Half-width (px) of the empty middle column the tiles leave between them. */
  innerEdgeX: number
}

/**
 * How much the game has scaled its UI down to fit this window. The smaller of the two ratios, so the
 * whole layout stays on screen - which is why narrowing the window moves the answer tiles even when
 * the height is unchanged.
 */
export const getUnityUiScale = (width: number, height: number): number =>
  Math.min(width / REFERENCE_WIDTH, height / REFERENCE_HEIGHT)

/**
 * How much narrower the window is than the shape the layout was designed for, as a factor of at most
 * 1: exactly 1 while the height is the binding dimension, then falling away as the window narrows
 * past that point. Anything sized off the viewport height can multiply by this to shrink with the
 * width too, the way the game's own UI does, without changing what it does at ordinary windows.
 */
export const getUnityWidthSquash = (width: number, height: number): number =>
  getUnityUiScale(width, height) / (height / REFERENCE_HEIGHT)

/** Tracked-component id React sends so Unity can see the DOM health bar. */
export const UNITY_POINT_BAR_COMPONENT_ID = 'pointBar'

export const UNITY_SAFE_TOP_VAR = '--unity-safe-top'

/** Extra space under the 44px bar row before the Unity play area starts. */
export const UNITY_HEALTHBAR_CLEARANCE_PX = 0

export interface UnityPlayArea {
  left: number
  top: number
  width: number
  height: number
}

export interface UnityPlayAreaRect {
  left: number
  top: number
  width: number
  height: number
}

/**
 * Top of the Unity play area: the top of the bar. Starting under the 44px row
 * left a large gap above the question because Unity centers in the leftover canvas.
 */
export const getUnitySafeTopPx = (pointBar: HTMLElement | null): number => {
  if (pointBar == null) return 0
  const top = pointBar.getBoundingClientRect().top
  return Math.max(0, Math.round(top + UNITY_HEALTHBAR_CLEARANCE_PX))
}

export const getUnityPlayArea = (canvas: HTMLElement | null): UnityPlayArea => {
  if (canvas != null) {
    const rect = canvas.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) {
      return {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height
      }
    }
  }

  return {
    left: 0,
    top: 0,
    width: window.innerWidth,
    height: window.innerHeight
  }
}

/**
 * Convert a viewport rect into Unity's play-area space. When the bar sits above the canvas,
 * clamp it to the top edge so the question is drawn in-canvas (just under the real bar)
 * instead of at a negative Y that Unity would clip.
 */
export const toUnityPlayAreaPosition = (
  rect: UnityPlayAreaRect,
  playArea: UnityPlayArea,
  clampTopToPlayArea = false
): { x: number, y: number, width: number, height: number } => {
  let x = rect.left - playArea.left
  let y = rect.top - playArea.top
  const width = rect.width
  let height = rect.height

  if (clampTopToPlayArea && y < 0) {
    height = Math.max(1, height + y)
    y = 0
  }

  return { x, y, width, height }
}

export const getUnityAnswerLayout = (
  width: number = window.innerWidth,
  height: number = window.innerHeight
): UnityAnswerLayout => {
  const scale = getUnityUiScale(width, height)
  const centre = height / 2
  const gapCentreY = centre + ANSWER_GAP_CENTRE_OFFSET * scale

  return {
    gapCentreY,
    answersTopY: centre + ANSWER_AREA_TOP_OFFSET * scale,
    bandTopY: gapCentreY + ANSWER_BAND_HALF_HEIGHT * scale,
    bandBottomY: gapCentreY - ANSWER_BAND_HALF_HEIGHT * scale,
    innerEdgeX: ANSWER_INNER_EDGE * scale
  }
}
